import React, { useState, useRef } from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const ReviewsSectionMobile = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}

  // Check if we're currently on the /review page
  const isOnReviewsPage = typeof window !== "undefined" && window.location.pathname.includes("/review")

  const showRatings = settings.showRatings || true
  const selectedReviewIds = settings.selectedReviewIds || []
  const showDates = settings.showDates || true
  const showOverallRating = settings.showOverallRating || true
  const showCTA = settings.showCTA || true
  const ctaText = settings.ctaText || "View all reviews"
  const showBackgroundColor = settings.showBackgroundColor || false
  const backgroundColor = settings.backgroundColor || "#ffffff"
  const showTextColor = settings.showTextColor || false
  const textColor = settings.textColor || "#55433f"
  const layout = settings.layout || "cards"

  // Helper function to get the correct text color
  const getTextColor = (defaultColor) => (showTextColor ? textColor : defaultColor)

  // Video review settings
  const showVideoReviews = settings.showVideoReviews !== false // Default to true
  const videoReviewsCount = settings.videoReviewsCount || 3
  const selectedVideoReviewIds = settings.selectedVideoReviewIds || []

  // Text review settings
  const showTextReviews = settings.showTextReviews !== false // Default to true
  const textReviewsCount = settings.textReviewsCount || 3

  // Get text reviews (filter out those with video reviews)
  const allTextReviews = (practice?.reviews || []).filter((review) => !review.hasCompletedVideoReview)
  const selectedTextReviews = isOnReviewsPage
    ? allTextReviews // Show all text reviews when on reviews page
    : selectedReviewIds.length > 0
    ? allTextReviews.filter((review) => selectedReviewIds.includes(review.id))
    : allTextReviews.slice(0, textReviewsCount)

  // Get video reviews
  const allVideoReviews = practice?.videoReviews || []
  const selectedVideoReviews = isOnReviewsPage
    ? allVideoReviews // Show all video reviews when on reviews page
    : selectedReviewIds.length > 0
    ? allVideoReviews.filter((review) => selectedVideoReviewIds.includes(review.id))
    : allVideoReviews.slice(0, videoReviewsCount)

  // Combined reviews for overall rating (use all reviews including those with video reviews)
  const allReviews = practice?.reviews || []

  const averageRating =
    allReviews.length > 0
      ? (allReviews.reduce((sum, review) => sum + (review.rating || 0), 0) / allReviews.length).toFixed(1)
      : "0.0"

  const VideoReviewCard = ({ videoReview, index }) => {
    const [isMuted, setIsMuted] = useState(true)
    const videoRef = useRef(null)

    // Safety checks for data
    if (!videoReview || !videoReview.id) return null

    // Get client name and anonymization
    const clientName = videoReview.client?.firstName || "Anonymous"
    const clientLastName = videoReview.client?.lastName || ""
    const displayName = clientName !== "Anonymous" ? `${clientName} ${clientLastName[0] || ""}.` : "Anonymous"

    // Get rating from review if it exists
    const rating = videoReview.review?.rating || 5

    // Get date - use review date if available, otherwise video review date
    const reviewDate = videoReview.review?.createdAt || videoReview.createdAt
    const formattedDate = reviewDate
      ? new Date(reviewDate)
          .toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric"
          })
          .toUpperCase()
      : ""

    // Get booking count
    const bookingCount = videoReview.timesBooked || videoReview.times_booked || 0

    // Handle hover events for autoplay
    const handleMouseEnter = () => {
      if (videoRef.current) {
        videoRef.current.muted = true // Always ensure muted
        setIsMuted(true) // Update state to match
        videoRef.current.play()
      }
    }

    const handleMouseLeave = () => {
      if (videoRef.current) {
        videoRef.current.pause()
        videoRef.current.currentTime = 0 // Reset to beginning
      }
    }

    // Handle mute toggle
    const handleMuteToggle = (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (videoRef.current) {
        videoRef.current.muted = !videoRef.current.muted
        setIsMuted(videoRef.current.muted)
      }
    }

    return (
      <div
        key={index}
        className="relative cursor-pointer overflow-hidden rounded-lg"
        style={{ width: "12rem", height: "20rem" }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}>
        {/* Booking Count Badge */}
        {bookingCount > 0 && (
          <div className="absolute left-2.5 top-2.5 z-10 rounded bg-black/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow">
            BOOKED {bookingCount} TIMES
          </div>
        )}

        {/* Video Player */}
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          src={videoReview.videoUrl || videoReview.video_url}
          poster={videoReview.thumbnailUrl}
          muted
          loop
          playsInline
          onLoadedMetadata={(e) => {
            // Set a default poster frame if no thumbnail
            if (!videoReview.thumbnailUrl) {
              e.target.currentTime = 1
            }
          }}
        />

        {/* Overlay Content */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

        {/* Bottom Content */}
        <div className="absolute inset-x-0 bottom-0 p-3 text-white">
          {/* Client Name */}
          <h3
            className="mb-1 text-[12px] font-normal"
            style={{
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
              color: "#FFFFFF"
            }}>
            {displayName}
          </h3>

          {/* Rating Stars */}
          {showRatings && (
            <div className="mb-1 flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <svg
                  key={i}
                  className="h-3 w-3"
                  fill={i < rating ? "#FF9529" : "none"}
                  stroke="#FF9529"
                  strokeWidth="1"
                  viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
          )}

          {/* Date */}
          {showDates && formattedDate && (
            <p
              className="text-[10px] font-semibold uppercase tracking-wide"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                color: "#FFFFFF"
              }}>
              {formattedDate}
            </p>
          )}
        </div>

        {/* Mute/Unmute Button */}
        <button
          className="absolute bottom-4 right-4 flex h-5 w-5 items-center justify-center rounded-full text-white transition-colors"
          onClick={handleMuteToggle}>
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
            {isMuted ? (
              <path
                fillRule="evenodd"
                d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.814L4.9 14H2a1 1 0 01-1-1V7a1 1 0 011-1h2.9l3.483-2.814a1 1 0 011.617.814zM12.146 5.146a1 1 0 011.414 0L15 6.586l1.44-1.44a1 1 0 111.414 1.414L16.414 8l1.44 1.44a1 1 0 01-1.414 1.414L15 9.414l-1.44 1.44a1 1 0 01-1.414-1.414L13.586 8l-1.44-1.44a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            ) : (
              <path
                fillRule="evenodd"
                d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.814L4.9 14H2a1 1 0 01-1-1V7a1 1 0 011-1h2.9l3.483-2.814a1 1 0 011.617.814zm7.133 2.09a1 1 0 011.414 0 6.018 6.018 0 010 8.51 1 1 0 11-1.414-1.414 4.018 4.018 0 000-5.682 1 1 0 010-1.414zm-2.37 2.37a1 1 0 011.414 0 2.018 2.018 0 010 2.828 1 1 0 11-1.414-1.414.018.018 0 000-.026 1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            )}
          </svg>
        </button>
      </div>
    )
  }

  const TextReviewCard = ({ review, index, isExpanded = false }) => {
    const [expanded, setExpanded] = useState(isExpanded)

    // Get card styling based on layout
    const getCardClasses = () => {
      switch (layout) {
        case "cards":
          return "flex flex-col gap-4 border border-gray-200 bg-white px-6 py-4"
        case "reviews":
          return "flex flex-col gap-3 border border-gray-200 bg-white px-4 py-3"
        case "list":
          return "flex flex-row gap-3 border-b border-gray-200 bg-white px-3 py-2"
        default:
          return "flex flex-col gap-4 border border-gray-200 bg-white px-6 py-4"
      }
    }

    const reviewDate = review.createdAt
      ? new Date(review.createdAt)
          .toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric"
          })
          .toUpperCase()
      : ""

    // Get client name with anonymization
    const isAnonymous = review.isAnonymous
    const name = review.client?.firstName || review.user?.firstName || "Anonymous"
    const lastName = review.client?.lastName || review.user?.lastName
    const firstName = name && name.split(" ")[0]
    const lastInitial = (lastName && `${lastName[0]}.`?.toUpperCase()) || (name && name.split(" ")[1]?.toUpperCase())
    const displayName = isAnonymous ? "Anonymous" : `${firstName} ${lastInitial || ""}`.trim()

    // Get booking count
    const bookingCount = review.times_booked || review.timesBooked || 0

    // Review text handling
    const reviewText = review.review?.trim() || review.content?.trim() || ""
    const maxLength = 200
    const shouldTruncate = reviewText.length > maxLength
    const displayText = expanded || !shouldTruncate ? reviewText : `${reviewText.substring(0, maxLength)}...`

    return (
      <div
        key={index}
        className={getCardClasses()}
        style={{
          borderColor: theme?.colors?.border || "#E6E6E6",
          maxWidth: "720px"
        }}>
        {/* Header Section */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            {/* Name and Stars */}
            <div className="flex flex-col gap-2">
              <h3
                className="text-[16px] font-semibold"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  color: theme?.colors?.textDark || "#454A4F"
                }}>
                {displayName}
              </h3>

              {showRatings && (
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className="h-4 w-4"
                      fill={i < (review.rating || 0) ? "#FF9529" : "none"}
                      stroke="#FF9529"
                      strokeWidth="1"
                      viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              )}
            </div>

            {/* Date */}
            {showDates && reviewDate && (
              <span
                className="text-[10px] font-semibold uppercase tracking-wider"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  color: theme?.colors?.textDark || "#454A4F"
                }}>
                {reviewDate}
              </span>
            )}
          </div>

          {/* Review Text */}
          <div className="flex flex-col gap-3">
            <p
              className="text-[14px] leading-relaxed"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                color: theme?.colors?.textDark || "#454A4F"
              }}>
              {displayText}
              {shouldTruncate && !expanded && (
                <button
                  onClick={() => setExpanded(true)}
                  className="ml-1 text-[12px] font-semibold underline"
                  style={{
                    color: theme?.colors?.primary || "#BD654F",
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                  }}>
                  Show more
                </button>
              )}
            </p>

            {/* Booking Count Badge */}
            {bookingCount > 0 && (
              <div
                className="inline-flex w-fit items-center justify-center rounded px-2 py-1 text-[10px] font-semibold uppercase tracking-wider"
                style={{
                  backgroundColor: theme?.colors?.backgroundLight || "#F4ECE9",
                  color: theme?.colors?.textDark || "#55433F",
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                }}>
                BOOKED {bookingCount} TIMES
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <section
      className={twMerge(
        "relative flex min-h-[400px] items-center justify-center py-12",
        isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
      )}
      style={{ backgroundColor: showBackgroundColor ? backgroundColor : theme?.colors?.background || "#FFFFFF" }}>
      <AnimatedSection className="mx-auto max-w-6xl px-5">
        <div className="flex flex-col items-center gap-12">
          {/* Header Section */}
          <div className="flex flex-col items-center gap-4">
            {/* Verified Badge */}
            <div className="flex items-center gap-3">
              <div className="relative h-6 w-6">
                <img src="/images/HealmeLogo.png" alt="Heal.me" className="h-full w-full object-contain" />
                {/* Checkmark positioned at bottom right */}
                <div
                  className="absolute -bottom-1 -right-1 flex h-3 w-3 items-center justify-center rounded-full shadow-sm"
                  style={{ backgroundColor: "#0D9DA4" }}>
                  <svg className="h-2 w-2" fill="white" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              <div className="flex items-center gap-1 rounded-full px-0 py-1">
                <span
                  className="text-[10px] font-semibold uppercase tracking-wider"
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    color: getTextColor(theme?.colors?.textDark || "#55433F")
                  }}>
                  Heal.me verified Reviews
                </span>
              </div>
            </div>

            {/* Title */}
            <h2
              className="text-[27px] font-bold leading-[32px]"
              style={{
                fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: getTextColor(theme?.colors?.textDark || "#55433F")
              }}>
              What clients are saying
            </h2>

            {/* Overall Rating */}
            {showOverallRating && (selectedTextReviews.length > 0 || selectedVideoReviews.length > 0) && (
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className="h-5 w-5"
                      fill={i < parseFloat(averageRating) ? "#FF9529" : "none"}
                      stroke="#FF9529"
                      strokeWidth="1"
                      viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <span
                  className="text-[10px] font-semibold uppercase tracking-wider"
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    color: getTextColor(theme?.colors?.textDark || "#454A4F")
                  }}>
                  {selectedTextReviews.length + selectedVideoReviews.length} Reviews
                </span>
              </div>
            )}

            {/* Top CTA Button */}
            {showCTA && allReviews.length > 0 && !isOnReviewsPage && (
              <button
                onClick={() => (window.location.href = "/review")}
                className="inline-flex items-center justify-center px-8 py-2 text-[14px] font-normal transition-all"
                style={{
                  backgroundColor: theme?.colors?.primary || "#BD654F",
                  color: "#F4ECE9",
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                }}>
                {ctaText}
              </button>
            )}
          </div>

          {/* Content Section */}
          <div className="flex flex-col gap-8">
            {/* Video Reviews Section */}
            {showVideoReviews && selectedVideoReviews.length > 0 && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col items-center gap-4">
                  {selectedVideoReviews.map((videoReview, index) => (
                    <VideoReviewCard key={videoReview.id} videoReview={videoReview} index={index} />
                  ))}
                </div>
              </div>
            )}

            {/* Text Reviews Section */}
            {showTextReviews && selectedTextReviews.length > 0 && (
              <div className="flex flex-col items-center gap-6">
                <div
                  className={`w-full max-w-[720px] ${
                    layout === "cards"
                      ? "flex flex-col gap-3"
                      : layout === "reviews"
                      ? "flex flex-col gap-3"
                      : layout === "list"
                      ? "flex flex-col gap-2"
                      : "flex flex-col gap-3"
                  }`}>
                  {selectedTextReviews.map((review, index) => (
                    <TextReviewCard key={review.id} review={review} index={index} />
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {(!showVideoReviews || selectedVideoReviews.length === 0) &&
              (!showTextReviews || selectedTextReviews.length === 0) && (
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                  <div className="text-4xl">⭐</div>
                  <h3
                    className="text-[20px] font-semibold"
                    style={{
                      fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                      color: getTextColor(theme?.colors?.textDark || "#55433F")
                    }}>
                    No reviews yet
                  </h3>
                  <p
                    className="text-[14px] text-gray-600"
                    style={{
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                    }}>
                    Reviews will appear here once clients leave them
                  </p>
                </div>
              )}
          </div>
        </div>
      </AnimatedSection>
    </section>
  )
}

export default ReviewsSectionMobile
